import React, { memo, useEffect, useState } from 'react'
import { IInputSchema } from '../types'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { buildControlsExt, numberInput } from '../../inputs/controls'
import { numberInputErrors } from '../../../utils/validationErrors/numberInputErrors'
import { inputDataDispatcher } from '../../../utils/InputDataDispatcher'
import {
    selectConfigBit,
    selectConfigIsChecked,
} from '../../../store/selectors/configSelectors'
import { PATH_JOINER } from '../utils/getConfigBitPath'
import { initInputData } from '../../../utils/InitInputData'
import { getLabel } from '../../../utils/getLabel'

export interface INumSchema extends IInputSchema {
    greater_than?: number | string
    greater_or_equal?: number | string
    less_than?: number | string
    less_or_equal?: number | string
}

interface NumProps {
    dataPath: string // dataPath, not including the name of this component
    pathWithElementID: string | undefined
    dataSchema: INumSchema
    name: string // key in schema
    wrapper?: string // e.g. 'block_dictionary'
}

export const Num = memo(function Num({
    dataSchema,
    name,
    dataPath,
    wrapper,
    pathWithElementID,
}: NumProps) {
    const dispatch = useAppDispatch()
    const configBit = useAppSelector(
        selectConfigBit(dataPath.split(PATH_JOINER))
    )
    const path = [...dataPath.split(PATH_JOINER), name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectConfigIsChecked)
    const {
        is_required: isRequired,
        default: defaultValue,
        description,
        name: inputLabel,
        type,
    } = dataSchema
    const validationCheck = inputValidationCheck(
        type,
        dataSchema,
        configBit?.[name]
    )
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck }, name)
    const [guid, , tabName] = path
    const label = getLabel(name, inputLabel)
    const className = !wrapper ? 'w-100' : ''
    const errorKey = pathWithElementID
        ? [...pathWithElementID?.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty = Object.keys(configBit || {}).length
    const isUndefined = typeof configBit?.[name] === 'undefined'
    const isEmpty = !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (isChecked[guid] && dataPath?.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    guid,
                    errorKey,
                    data: configBit,
                    setInputState,
                    tabName,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    guid,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked[guid]]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                data: configBit,
                name,
                path,
                dataSchema,
                inputState,
                validationCheck,
                guid,
                errorKey,
                isUndefined,
                checkPath,
                type,
            },
            dispatch
        )
        setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        numberInput({
            name: name,
            description,
            label,
            className,
            isColumn: true,
            errMsg: numberInputErrors(inputState?.[name], dataSchema),
            isRequired,
            placeholder: defaultValue,
            onBlur: handleOnBlur,
        }),
    ]

    const NumInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{NumInput}</div>
})
